import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, FileText, File } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import VoicePlaybackButton from './VoicePlaybackButton';

/**
 * Renders a single chat message bubble (user or assistant).
 *
 * Assistant messages get the action row: voice playback, copy, download menu.
 */
const MessageBubble = ({
  message,
  index,
  isCopied,
  language,
  ttsFetcher,
  autoplay = false,
  autoplayStamp = 0,
  onCopy,
  onDownloadTxt,
  onDownloadPdf,
  onDownloadDocx,
}) => {
  const { t } = useTranslation();
  const isAssistant = message.role === 'assistant';
  // Badge by actual TTS language (stored on assistant message), falling back to UI lang
  const ttsLang = message.tts_language || language;
  const voiceBadge = ttsLang === 'en' ? t('voice.languageBadgeEn') : t('voice.languageBadgePl');

  return (
    <div
      className={`hussar-message ${isAssistant ? 'hussar-message-assistant' : 'hussar-message-user'}`}
    >
      <div className="hussar-message-content">{message.content}</div>

      {isAssistant && (
        <div className="hussar-message-actions">
          <span className="hussar-voice-badge" data-testid="voice-language-badge">
            {voiceBadge}
          </span>
          <VoicePlaybackButton
            text={message.content}
            language={ttsLang}
            fetcher={ttsFetcher}
            autoplay={autoplay}
            autoplayStamp={autoplayStamp}
            messageId={message.message_id}
            conversationId={message.conversation_id}
            inputTypeOrigin={message.first_user_input_type}
            compact
          />

          <Button
            onClick={() => onCopy(message.content, index)}
            className="hussar-btn-action"
            title={t('app.copy')}
            data-testid="copy-message-btn"
          >
            {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="hussar-btn-action"
                title={t('app.download')}
                data-testid="download-menu-trigger"
              >
                <img
                  src="/hussarai-logo.png"
                  alt={t('app.download')}
                  className="w-4 h-4 object-contain"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-900 border border-zinc-800 text-zinc-100"
            >
              <DropdownMenuItem
                onClick={() => onDownloadTxt(message.content)}
                className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-100"
                data-testid="download-txt-item"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('app.downloadTxt')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDownloadPdf(message.content)}
                className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-100"
                data-testid="download-pdf-item"
              >
                <File className="w-4 h-4 mr-2" />
                {t('app.downloadPdf')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDownloadDocx(message.content)}
                className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-100"
                data-testid="download-docx-item"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('app.downloadDocx')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
